import { AxiosError } from 'axios'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { setToast } from '@/entities/toast'
import { usePostApiOnboardingOnboardClient } from '@/shared/apis/fastmile/generated/onboarding/onboarding'
import {
  OnboardingDepotDto, OnboardingDriverDto, OnboardingGroupDto, OnboardingPreferencesDto, OnboardingRequestDto, OnboardingRunDto,
} from '@/shared/apis/fastmile/models'
import { ApiErrorResponse } from '@/shared/apis/fastmile/models/ApiErrorResponse'
import { Button, Icon } from '@/shared/components'
import { DEFAULT_MAP_CENTER_LAT, DEFAULT_MAP_CENTER_LON } from '@/shared/config/consts'
import { useAppDispatch } from '@/shared/store'
import { pxToRem } from '@/shared/styles/themes'
import { faArrowRight, faCheck, faChevronLeft, faChevronRight } from '@fortawesome/pro-regular-svg-icons'
import { Box, Paper, Step, StepLabel, Stepper, styled, Typography } from '@mui/material'

import { ACTIVE_STEP_KEY, ONBOARDING_STATE_KEY, useOnboardingState } from './libs/useOnboardingState'
import { DepotFormValues, DriverFormValues, GeneralPreferencesFormValues, GroupFormValues, RunFormValues } from './models/OnboardingFormFields'
import { DepotSetupForm } from './OnboardingSteps/DepotSetupForm'
import { DriverSetupForm } from './OnboardingSteps/DriverSetupForm'
import { GeneralPreferencesForm } from './OnboardingSteps/GeneralPreferencesForm'
import { GroupSetupForm } from './OnboardingSteps/GroupSetupForm'
import { OnboardingSummary } from './OnboardingSteps/OnboardingSummary'
import { RunSetupForm } from './OnboardingSteps/RunSetupForm'
import { WelcomeOverlay } from './WelcomeOverlay'

// Define the steps for the onboarding process
const steps = [
  'Preferences',
  'Group Setup',
  'Depot Setup',
  'Run Setup',
  'Driver Setup',
  'Summary',
]

export type OnboardingPageProps = {
  //
}

export const OnboardingPage: React.FC<OnboardingPageProps> = () => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showWelcomeOverlay, setShowWelcomeOverlay] = useState(false)

  const {
    activeStep,
    setActiveStep,
    completed,
    setCompleted,
    skipped,
    setSkipped,
    generalPreferences,
    setGeneralPreferences,
    groupData,
    setGroupData,
    depotData,
    setDepotData,
    runData,
    setRunData,
    driverData,
    setDriverData,
  } = useOnboardingState()

  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  // Initialize the onboarding mutation
  const { mutateAsync: onboardClient } = usePostApiOnboardingOnboardClient()

  const isStepOptional = (step: number) => {
    return step > 3 && step < 5
  }

  const isStepSkipped = (step: number) => {
    return skipped.has(step)
  }

  const handleNext = () => {
    let newSkipped = skipped

    if (isStepSkipped(activeStep)) {
      newSkipped = new Set(newSkipped.values())
      newSkipped.delete(activeStep)
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1)
    setSkipped(newSkipped)
  }

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1)
  }

  const handleSkip = () => {
    if (!isStepOptional(activeStep)) {
      throw new Error('You can\'t skip a step that isn\'t optional.')
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1)
    setSkipped((prevSkipped) => {
      const newSkipped = new Set(prevSkipped.values())

      newSkipped.add(activeStep)

      return newSkipped
    })
  }

  const handleComplete = () => {
    const newCompleted = { ...completed }

    newCompleted[activeStep] = true
    setCompleted(newCompleted)
    handleNext()
  }

  const handleSaveGeneralPreferences = (data: GeneralPreferencesFormValues) => {
    setGeneralPreferences(data)
    handleComplete()
  }

  const handleSaveGroupSetup = (data: GroupFormValues) => {
    const newData = {
      ...data,
      id: 'group-' + Date.now().toString(),
    }

    setGroupData(newData)
    handleComplete()
  }

  const handleSaveDepotSetup = (data: DepotFormValues) => {
    const newData = {
      ...data,
      id: 'depot-' + Date.now().toString(),
      groupId: groupData.id,
    }

    setDepotData(newData)
    handleComplete()
  }

  const handleSaveRunSetup = (data: RunFormValues) => {
    const newData = {
      ...data,
      id: 'run-' + Date.now().toString(),
      groupId: groupData.id,
      startDepotId: depotData.id,
      finishDepotId: depotData.id,
    }

    setRunData(newData)
    handleComplete()
  }

  const handleSaveDriverSetup = (data: DriverFormValues) => {
    const newData = {
      ...data,
      id: 'driver-' + Date.now().toString(),
    }

    setDriverData(newData)
    handleComplete()
  }

  const handleSubmitOnboardingData = async () => {
    setIsSubmitting(true)

    try {
      // Transform the form data into the expected OnboardingModel format
      const preferences: OnboardingPreferencesDto = {
        businessModel: generalPreferences.businessModel,
        longitude: groupData.mapCenter?.longitude?.toString() || DEFAULT_MAP_CENTER_LON.toString(),
        latitude: groupData.mapCenter?.latitude?.toString() || DEFAULT_MAP_CENTER_LAT.toString(),
        distanceUnit: generalPreferences.distanceUnit,
        weightUnit: generalPreferences.weightUnit,
        volumeUnit: generalPreferences.volumeUnit,
      }

      const group: OnboardingGroupDto = {
        groupName: groupData.name,
        timeZone: groupData.timeZone?.value?.toString() || '',
        longitude: groupData.mapCenter?.longitude?.toString() || DEFAULT_MAP_CENTER_LON.toString(),
        latitude: groupData.mapCenter?.latitude?.toString() || DEFAULT_MAP_CENTER_LAT.toString(),
        groupId: 0,
      }

      const depot: OnboardingDepotDto = {
        depotCode: depotData.externalId,
        depotName: depotData.name,
        depotColor: depotData.uiColor?.value?.toString(),
        street: depotData.address?.street || null,
        suburb: depotData.address?.suburb || null,
        postCode: depotData.address?.postcode || null,
        state: depotData.address?.state || null,
        country: depotData.address?.country || null,
        latitude: depotData.address?.y,
        longitude: depotData.address?.x,
      }

      const run: OnboardingRunDto = {
        runName: runData.name,
        description: runData.description,
        runColor: runData.uiColorId?.value?.toString(),
        weightCapacity: runData.weightCapacity || 0,
        volumeCapacity: runData.volumeCapacity || 0,
        runId: 0,
      }

      // Skip driver if it was skipped during onboarding
      const driver: OnboardingDriverDto | undefined = isStepSkipped(4) ? undefined : {
        login: driverData.login,
        name: driverData.name,
        password: driverData.password,
        mobile: driverData.mobile,
        email: driverData.email,
        employeeNumber: driverData.employeeNumber,
      }

      // Create the onboarding payload
      const onboardingPayload: OnboardingRequestDto = {
        isDemoGroupCreationEnabled: false,
        preferences,
        group: group,
        depots: [depot],
        runs: [run],
        driver,
      }

      // Call the API
      await onboardClient({ data: onboardingPayload })

      // Clear localStorage onboarding data on successful completion
      localStorage.removeItem(ONBOARDING_STATE_KEY)
      localStorage.removeItem(ACTIVE_STEP_KEY)

      setIsSubmitting(false)
      setShowWelcomeOverlay(true)
    } catch (error) {
      dispatch(setToast({
        open: true,
        severity: 'error',
        title: 'Error',
        content: (error as AxiosError<ApiErrorResponse>)?.response?.data?.detail || 'There was an error setting up your account. Please try again.',
      }))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleNavigateToOperations = () => {
    navigate('/operations')
  }

  const handleShowUpgrade = () => {
    navigate('/upgrade?from=onboarding')
  }

  return (
    <StyledContainer>
      <StyledHeader>
        <StyledTitle variant="h1">Welcome to FastMile</StyledTitle>
        <Typography variant="body1" color="text.secondary">
          Let's set up your account to get you started. You can skip optional steps if needed.
        </Typography>
      </StyledHeader>

      <StyledContent>
        <StyledStepper activeStep={activeStep}>
          {steps.map((label, index) => {
            const stepProps: { completed?: boolean } = {}
            const labelProps: { optional?: React.ReactNode } = {}

            if (isStepOptional(index)) {
              labelProps.optional = <Typography variant="caption">Optional</Typography>
            }

            if (isStepSkipped(index)) {
              stepProps.completed = false
            }

            return (
              <Step key={label} {...stepProps}>
                <StepLabel {...labelProps}>{label}</StepLabel>
              </Step>
            )
          })}
        </StyledStepper>

        <StyledFormContainer>
          {activeStep === 0 && (
            <GeneralPreferencesForm
              initialValues={generalPreferences}
              onSubmit={handleSaveGeneralPreferences}
            />
          )}

          {activeStep === 1 && (
            <GroupSetupForm
              initialValues={groupData}
              onSubmit={handleSaveGroupSetup}
              isSubmitting={isSubmitting}
            />
          )}

          {activeStep === 2 && (
            <DepotSetupForm
              initialValues={depotData}
              groupData={groupData}
              onSubmit={handleSaveDepotSetup}
              isSubmitting={isSubmitting}
            />
          )}

          {activeStep === 3 && (
            <RunSetupForm
              initialValues={runData}
              generalPreferencesData={generalPreferences}
              groupData={groupData}
              depotData={depotData}
              onSubmit={handleSaveRunSetup}
              isSubmitting={isSubmitting}
            />
          )}

          {activeStep === 4 && (
            <DriverSetupForm
              initialValues={driverData}
              groupData={groupData}
              runData={runData}
              onSubmit={handleSaveDriverSetup}
              isSubmitting={isSubmitting}
            />
          )}

          {activeStep === 5 && (
            <OnboardingSummary
              generalPreferences={generalPreferences}
              groupData={groupData}
              depotData={depotData}
              runData={runData}
              driverData={driverData}
              skippedSteps={skipped}
              isSubmitting={isSubmitting}
            />
          )}
        </StyledFormContainer>

        <StyledFooter>
          <Button
            outlined
            startIcon={<Icon icon={faChevronLeft} />}
            onClick={handleBack}
            disabled={activeStep === 0}
          >
            Back
          </Button>

          <Box sx={{ display: 'flex', gap: pxToRem(8) }}>
            {isStepOptional(activeStep) && (
              <Button
                text
                startIcon={<Icon icon={faArrowRight} />}
                onClick={handleSkip}
                disabled={isSubmitting}
              >
                Skip
              </Button>
            )}

            {activeStep === steps.length - 1 ? (
              <Button
                primary
                startIcon={<Icon icon={faCheck} />}
                onClick={handleSubmitOnboardingData}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Finish Setup'}
              </Button>
            ) : (
              <Button
                primary
                endIcon={<Icon icon={faChevronRight} />}
                type={activeStep < steps.length - 1 ? 'submit' : 'button'}
                form={`onboarding-step-${activeStep}`}
                disabled={isSubmitting}
              >
                {activeStep === steps.length - 1 ? 'Finish' : 'Save & Continue'}
              </Button>
            )}
          </Box>
        </StyledFooter>
      </StyledContent>

      <WelcomeOverlay
        show={showWelcomeOverlay}
        onFinish={handleNavigateToOperations}
        onUpgrade={handleShowUpgrade}
      />

    </StyledContainer>
  )
}

const StyledContainer = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background-color: ${props => props.theme.palette.background.default};
  overflow: auto;
`

const StyledHeader = styled('div')`
  padding: ${pxToRem(32)} ${pxToRem(48)};
  background-color: ${props => props.theme.palette.background.paper};
  border-bottom: 1px solid ${props => props.theme.palette.divider};
`

const StyledTitle = styled(Typography)`
  font-size: ${pxToRem(28)};
  font-weight: 500;
  margin-bottom: ${pxToRem(8)};
  color: ${props => props.theme.palette.text.primary};
`

const StyledContent = styled(Paper)`
  margin: 0;
  padding: 0;
  border-radius: ${pxToRem(8)};
  display: flex;
  flex-direction: column;
  flex: 1;
  max-width: ${pxToRem(960)};
  align-self: center;
  width: 100%;

  ${props => props.theme.breakpoints.up('md')} {
    margin: ${pxToRem(24)} ${pxToRem(48)};
    padding: ${pxToRem(24)};
  }
`

const StyledStepper = styled(Stepper)`
  padding: 0 ${pxToRem(16)};
  margin-bottom: ${pxToRem(32)};

  ${props => props.theme.breakpoints.up('md')} {
    padding: 0;
  }
`

const StyledFormContainer = styled('div')`
  flex: 1;
  padding: ${pxToRem(16)};
  min-height: ${pxToRem(400)};

  ${props => props.theme.breakpoints.up('md')} {
    padding: ${pxToRem(16)} 0;
  }
`

const StyledFooter = styled('div')`
  margin-top: ${pxToRem(24)};
  padding: ${pxToRem(16)};
  border-top: 1px solid ${props => props.theme.palette.divider};
  display: flex;
  justify-content: space-between;
  
  ${props => props.theme.breakpoints.up('md')} {
    padding: ${pxToRem(16)} 0 0 0;
  }
`